import React from 'react'

import { isAxiosError } from 'axios'
import getSymbolFromCurrency from 'currency-symbol-map'
import { useForm } from 'react-hook-form'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'

import { TeamRole } from '@pkg/types/team'

import {
  availableRoles,
  isFreeRole,
  isTeamVisibleForRole,
} from '@/helpers/team'
import { useBillingInfo, useTeam } from '@/react-query/team'
import { useSendTeamInvite } from '@/react-query/team-invites'
import { ArrowSvg } from '@/views/assets/ArrowSvg'
import { roleToLabel } from '@/views/screens/Team/const'

const emailPattern = /^\s*[^\s@]+@[^\s@]+\.[^\s@]+\s*$/

interface AddTeamMemberForm {
  email: string
}

export const InviteTeamMemberModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const billingInfo = useBillingInfo()
  const team = useTeam().data

  const defaultValues: AddTeamMemberForm = React.useMemo(
    () => ({
      email: '',
    }),
    [],
  )

  const [role, setRole] = React.useState<TeamRole>('MEMBER')

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    setError,
  } = useForm<AddTeamMemberForm>({
    defaultValues,
  })

  const sendTeamInvite = useSendTeamInvite()

  const handleAddTeamMemberFormSubmit = async ({
    email,
  }: AddTeamMemberForm) => {
    try {
      await sendTeamInvite.mutateAsync({ toEmail: email.trim(), role })
      onClose()
    } catch (error) {
      if (isAxiosError(error) && error.response?.data?.message) {
        setError('email', {
          type: 'manual',
          message: error.response.data.message,
        })
      } else {
        setError('email', {
          type: 'manual',
          message: 'Something went wrong',
        })
      }
    }
  }

  const newMemberSeesTeamInterviews = team
    ? isTeamVisibleForRole(team, role)
    : true

  const currencySymbol = getSymbolFromCurrency(
    billingInfo.data?.currency?.toUpperCase() || 'USD',
  )

  return (
    <Modal size={'3xl'} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay bg="whiteAlpha.600" backdropFilter={'auto'} />
      <ModalContent
        bgColor={'#f5f5f5'}
        boxShadow={'0px 4px 30px 0px rgba(0, 0, 0, 0.25)'}
        rounded={'2.5rem'}
        w={'480px'}
        p={3}
      >
        <form onSubmit={handleSubmit(handleAddTeamMemberFormSubmit)}>
          <ModalHeader>{'Invite new teammate'}</ModalHeader>
          <ModalCloseButton rounded={'full'} mt={5} mr={5} />
          <ModalBody display={'flex'} flexDirection={'column'}>
            <FormControl isInvalid={Boolean(errors.email)}>
              <FormLabel fontWeight={'400'} htmlFor="email" color={'black'}>
                Email
              </FormLabel>
              <Input
                id="email"
                required={true}
                placeholder="teammate@yourcompany.com"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: emailPattern,
                    message: 'This does not look like an email address',
                  },
                })}
                variant={'brand-solid-white'}
              />
              <FormErrorMessage>
                {errors.email && errors.email.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl mt={4}>
              <FormLabel fontWeight={'400'} htmlFor="role" color={'black'}>
                Role
              </FormLabel>
              <Menu>
                <MenuButton
                  rounded={'1.25rem'}
                  as={Button}
                  rightIcon={<ArrowSvg ml={4} verticalAlign={'middle'} />}
                  variant={'unstyled'}
                  px={4}
                  bgColor={'white'}
                  _hover={{
                    backgroundColor: '#E0E0E0',
                  }}
                  w={'100%'}
                  textAlign={'left'}
                  justifyContent={'space-between'}
                  display={'flex'}
                >
                  {roleToLabel[role]}
                </MenuButton>
                <MenuList w={'100%'}>
                  {availableRoles?.map((availableRole) => (
                    <MenuItem
                      key={availableRole}
                      onClick={() => {
                        setRole(availableRole)
                      }}
                      fontWeight={'normal'}
                      w={'408px'}
                    >
                      {roleToLabel[availableRole]}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </FormControl>

            {!isFreeRole(role) && (
              <Box mt={8}>
                This role will add {currencySymbol}
                {(billingInfo?.data?.price || 0) / 100} paid seat to your
                account.
                <br />
              </Box>
            )}
            <Box mt={6}>
              {newMemberSeesTeamInterviews
                ? 'All interviews in the group (including all yours) will become visible to the new participant.'
                : 'The new participant will see only the interviews they take part in. Other interviews in the group stay hidden from them.'}
            </Box>
          </ModalBody>

          <ModalFooter justifyContent={'flex-start'}>
            <Button
              type="submit"
              variant={'brand-solid'}
              isLoading={isSubmitting}
              rounded={'1.25rem'}
            >
              Invite
            </Button>
            <Button
              rounded={'1.25rem'}
              variant="brand-solid-white"
              onClick={onClose}
            >
              Cancel
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
